import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { debounce } from 'lodash';
import { Table, Heading, Flex } from '@oliasoft-open-source/react-ui-library';
import {
  removeTask,
  cellValueUpdated,
  updateTask,
  onTaskListReorder,
  reorderTimeTrackerList,
  addTasks,
  bulkUpdateActualDepth,
} from 'store/entities/time-tracker/time-tracker';
import { round, unum } from '@oliasoft-open-source/units';
import { autoSaveWait } from 'src/config/config';
import translations from 'src/internationalisation/translation-map.json';
import { buildOperation, buildActivity, buildHeaders } from 'views/time-tracker/operations-table-builder';
import { selectTimeTrackerLabelCofig } from 'store/entities/company-settings/selectors';
import { selectSectionsDataStructure } from 'src/store/entities/sections-data-structure/selector';
import { selectSimulationsResult } from 'store/entities/simulations/selectors';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { actualDepthArr } from './utils/actual-depth-propose';
import { downloadTimeTrackerExcel, uploadTimeTrackerExcel } from './utils/time-tracker-download';
import { initialTimeTrackerItem } from './utils/initial-time-tracker-item';
import { addTaskOptions } from './utils/consts';
import { findSectionItemNameById } from './utils/find-section-item-name';
import { getColumnAlignment } from './utils/get-tt-column-alignment';
import { getColumnWidth } from './utils/get-tt-column-width';
import { EditColumns } from './edit-columns';
import { AddTaskModal } from './add-task.modal';

const OperationsTable = ({
  itemsList,
  removeTask,
  cellValueUpdated,
  updateTask,
  disabledFields,
  labelConfig,
  onTaskListReorder,
  reorderTimeTrackerList,
  isPageDisabled,
  firstOperation,
  lastOperation,
  totalAFE,
  totalVariance,
  well,
  sections,
  operations,
  activities,
  simulationsResult,
  bulkUpdateActualDepth,
  addTasks,
  depthUnit,
}) => {
  const { t } = useTranslation();
  const { project: projectId } = useParams();
  const debounceUpdateTask = useRef(debounce(updateTask, 500));
  const debounceReorderTrackerList = useRef(debounce(reorderTimeTrackerList, autoSaveWait));
  const debounceBulkUpdateActualDepth = useRef(debounce(bulkUpdateActualDepth, autoSaveWait));

  const rows = [];

  const getActionP90 = (operationId) => {
    if (!operationId || !simulationsResult?.operations) return null;
    const operation = simulationsResult?.operations?.find((operation) => operation.id === operationId);
    return round(operation?.percentiles[89], 2);
  };

  const [selectedRowId, setSelectedRowId] = useState('');
  const [expandedOperationIds, setExpandedOperationIds] = useState([]);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [newActivity, setNewActivity] = useState(null);
  const actualDepthsRef = useRef([]);
  const previousDepthsRef = useRef([]);

  const onAddTask = ({ insertType, operation, activityIndex, operationIndex }) => {
    setNewActivity(
      initialTimeTrackerItem(
        insertType,
        operation,
        activityIndex,
        operation.activities[activityIndex],
        t,
        operationIndex,
      ),
    );
    setTaskModalVisible(true);
  };

  useEffect(() => {
    if (!expandedOperationIds?.length) setExpandedOperationIds(itemsList.map((o) => o.timeTrackerItemId));
  }, [itemsList]);

  const toggleSelectedRow = (activityId) =>
    selectedRowId === activityId ? setSelectedRowId('') : setSelectedRowId(activityId);

  const toggleExpandedRow = (operationId) =>
    expandedOperationIds.includes(operationId)
      ? setExpandedOperationIds(expandedOperationIds.filter((id) => id !== operationId))
      : setExpandedOperationIds([...expandedOperationIds, operationId]);

  useEffect(() => {
    const changedDepths = actualDepthsRef.current.filter((depth) => {
      const previousDepth = previousDepthsRef.current.find((item) => item.id === depth.id);
      return !previousDepth || previousDepth.value !== depth.value;
    });

    const updates = [];
    if (changedDepths.length > 0) {
      changedDepths.forEach((depth) => {
        if (depth.value && depth.id && depth.parentId) {
          const activityIndex = actualDepthsRef.current.findIndex((item) => item.id === depth.id);
          if (activityIndex !== -1 && !actualDepthsRef.current[activityIndex].actualDepth) {
            updates.push({
              timetrackeritemid: depth.id,
              actual_depth: depth.value,
            });
          }
        }
      });
    }
    if (updates.length > 0) {
      debounceBulkUpdateActualDepth.current(updates);
    }
    previousDepthsRef.current = [...actualDepthsRef.current];
  }, [itemsList]);

  itemsList.forEach((operation, operationIndex) => {
    rows.push({
      key: operation.timeTrackerItemId,
      noDrag: true,
      cells: buildOperation(
        {
          ...operation,
          operationName: findSectionItemNameById(operations, operation.sectionsOperationId, 'sectionsOperationId'),
          sectionName: findSectionItemNameById(
            sections,
            operation.sectionId || operation.section?.sectionMasterId,
            'sectionId',
          ),
          p90: getActionP90(operation.originId),
          plannedDepth: round(unum(operation.plannedDepth, depthUnit, 'm'), 2),
          actualDepth: round(unum(operation.actualDepth, depthUnit, 'm'), 2),
        },
        disabledFields,
        expandedOperationIds,
      ),
      onRowClick: () => toggleExpandedRow(operation.timeTrackerItemId),
    });

    operation.activities.forEach((activity, activityIndex) => {
      if (expandedOperationIds.includes(activity.parentId)) {
        let actualDepthsPropose = [];
        if (!activity?.actualDepth) {
          actualDepthsPropose = actualDepthArr(operation);
        }

        actualDepthsPropose.forEach((depth) => {
          if (depth && depth.id && depth.value) {
            const existingDepthIndex = actualDepthsRef.current.findIndex((item) => item.id === depth.id);
            if (existingDepthIndex !== -1) {
              actualDepthsRef.current[existingDepthIndex] = depth;
            } else {
              actualDepthsRef.current.push(depth);
            }
          }
        });

        rows.push({
          taskId: activity.timeTrackerItemId,
          parentId: activity?.parentId,
          active: selectedRowId === activity.timeTrackerItemId,
          onRowClick: () => toggleSelectedRow(activity.timeTrackerItemId),
          cells: buildActivity(
            {
              ...activity,
              activityName: findSectionItemNameById(
                activities,
                activity.sectionsOperationActivityId,
                'sectionsOperationActivityId',
              ),
              operationName: findSectionItemNameById(operations, operation.sectionsOperationId, 'sectionsOperationId'),
              sectionName: findSectionItemNameById(
                sections,
                operation.sectionId || operation.section?.sectionMasterId,
                'sectionId',
              ),
              p90: activity.ninthDecile ?? null,
              plannedDepth: round(unum(activity.plannedDepth, depthUnit, 'm'), 2),
              actualDepth: round(unum(activity.actualDepth, depthUnit, 'm'), 2),
            },
            actualDepthsPropose[activityIndex],
            debounceUpdateTask,
            cellValueUpdated,
            disabledFields,
            projectId,
            isPageDisabled,
            depthUnit,
          ),
          actions: [
            {
              label: t(translations.delete),
              subActions: [
                {
                  icon: 'add above',
                  label: t(translations.timeTracker_addTaskAbove),
                  onClick: () =>
                    onAddTask({
                      insertType: addTaskOptions.ABOVE,
                      operation,
                      activityIndex,
                      operationIndex,
                    }),
                  disabled: isPageDisabled,
                },
                {
                  icon: 'add below',
                  label: t(translations.timeTracker_addTaskBelow),
                  onClick: () =>
                    onAddTask({
                      insertType: addTaskOptions.BELOW,
                      operation,
                      activityIndex,
                      operationIndex,
                    }),
                  disabled: isPageDisabled,
                },
                {
                  icon: 'minus',
                  label: t(translations.delete),
                  onClick: () => removeTask(activity.timeTrackerItemId, operation.timeTrackerItemId),
                  disabled: isPageDisabled,
                },
              ],
            },
          ],
        });
      }
    });
  });
  const onListReorder = (reorderData) => {
    const { from, to } = reorderData;
    const { taskId: fromTaskId, parentId: fromParentId } = rows[from];
    const { taskId: toTaskId, parentId: toParentId } = rows[to];
    if (fromParentId === toParentId) {
      onTaskListReorder({ fromTaskId, toTaskId, parentId: toParentId });
      debounceReorderTrackerList.current(projectId, {
        fromTaskId,
        toTaskId,
        parentId: toParentId,
      });
    }
  };
  const tableWithoutData = {
    testId: 'operations-table',
    draggable: !isPageDisabled,
    actions: [
      {
        testId: 'operations-table-upload-button',
        icon: 'upload',
        tooltip: t(translations.timeTracker_upload),
        onClick: () => uploadTimeTrackerExcel(addTasks, projectId),
      },
    ],
    name: 'TIME TRACKER',
    actionsRight: true,
  };

  const table = {
    testId: 'operations-table',
    draggable: !isPageDisabled,
    actions: [
      {
        testId: 'operations-table-download-button',
        icon: 'download',
        tooltip: t(translations.timeTracker_download),
        onClick: () =>
          downloadTimeTrackerExcel({
            wellName: well.name,
            totalAFE,
            startTime: firstOperation?.start,
            estimatedFinish: lastOperation?.finish,
            variance: totalVariance,
            itemsList: itemsList.map((operation) => ({
              ...operation,
              ninthDecile: getActionP90(operation.originId),
            })),
            operations,
            activities,
            sections,
            lengthOfTimeTracker: rows.length - 1,
            depthUnit,
          }),
      },
      {
        testId: 'operations-table-upload-button',
        icon: 'upload',
        tooltip: t(translations.timeTracker_upload),
        onClick: () => uploadTimeTrackerExcel(addTasks, projectId),
      },
      {
        childComponent: <EditColumns />,
      },
    ],

    name: 'TIME TRACKER',
    actionsRight: true,
    columnWidths: getColumnWidth(disabledFields),
    columnAlignment: getColumnAlignment(disabledFields),
    headers: buildHeaders(disabledFields, labelConfig, depthUnit),
    rows,
  };
  return itemsList.length ? (
    <>
      {taskModalVisible && <AddTaskModal setTaskModalVisible={setTaskModalVisible} newActivity={newActivity} />}
      <Table table={table} onListReorder={onListReorder} />
    </>
  ) : (
    <>
      <Table table={tableWithoutData} />
      <Flex alignItems="center" justifyContent="center" height="calc(100% - 127px)">
        <Heading top>{t(translations.timeTracker_noOperationsFound)}</Heading>
      </Flex>
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const labelConfig = selectTimeTrackerLabelCofig(entities);
  const simulationsResult = selectSimulationsResult(entities);
  const { list, isFetching, disabledFields } = entities.timeTracker;
  const { sections, operations, activities } = selectSectionsDataStructure(entities);
  const { well } = entities.projects.overview;
  const firstOperation = list[0];
  const lastOperation = list?.slice(-1)[0];
  const totalAFE = round(list.reduce((acc, operation) => acc + operation.afe, 0) / 24, 2);
  const totalVariance = round(
    list?.reduce(
      (sum, operation) =>
        sum +
        operation.activities.reduce((acc, activity) => acc + (activity.actual ? activity.actual - activity.afe : 0), 0),
      0,
    ),
    2,
  );

  return {
    itemsList: list,
    isFetching,
    well,
    firstOperation,
    lastOperation,
    totalAFE,
    totalVariance,
    disabledFields,
    labelConfig,
    sections,
    operations,
    activities,
    simulationsResult,
  };
};

const mapDispatchToProps = {
  removeTask,
  cellValueUpdated,
  updateTask,
  onTaskListReorder,
  reorderTimeTrackerList,
  addTasks,
  bulkUpdateActualDepth,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(OperationsTable));

export { Container as OperationsTable };
